import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Cookie } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { posthog, POSTHOG_CONSENT_STORAGE_KEY } from '@/shared/lib/posthog';

type Consent = 'accepted' | 'rejected';

function readConsent(): Consent | null {
  try {
    const value = localStorage.getItem(POSTHOG_CONSENT_STORAGE_KEY);
    return value === 'accepted' || value === 'rejected' ? value : null;
  } catch {
    return null;
  }
}

export function CookieBanner() {
  const { t } = useTranslation();
  const [consent, setConsent] = useState<Consent | null>(readConsent);

  if (consent) return null;

  function choose(value: Consent) {
    try {
      localStorage.setItem(POSTHOG_CONSENT_STORAGE_KEY, value);
    } catch {
      // localStorage unavailable — still dismiss for this session
    }
    if (value === 'accepted') {
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
    }
    setConsent(value);
  }

  return (
    <div
      role="dialog"
      aria-label={t('cookie.ariaLabel')}
      className="fixed bottom-0 left-0 right-0 z-50 border-t bg-card px-4 py-3 shadow-lg md:px-6"
    >
      <div className="mx-auto flex w-full max-w-screen-2xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 text-sm text-muted-foreground">
          <Cookie className="mt-0.5 size-4 shrink-0 text-foreground" aria-hidden />
          <p>
            {t('cookie.notice')}{' '}
            <Link
              to="/privacy-policy"
              className="underline underline-offset-4 hover:text-foreground"
            >
              {t('footer.privacy')}
            </Link>
            .
          </p>
        </div>
        <div className="flex shrink-0 gap-2 self-end sm:self-auto">
          <Button variant="ghost" onClick={() => choose('rejected')}>
            {t('cookie.reject')}
          </Button>
          <Button variant="outline" onClick={() => choose('accepted')}>
            {t('cookie.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
}
